import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import { usePersona } from '@/app/providers';
import type { Capability, Persona, Role } from '@/shared/types';

// =========================================================
// Role context
// =========================================================
// Layered on top of `usePersona()` so that visibility-gating reads from the
// active *role* and its capabilities, not from the persona display name. The
// persona-to-role mapping is a sensible default for the mock; a real build
// would let an admin override it per user. The mapping lives here in one
// place so the rest of the codebase only ever asks `can(...)`.
//
// Spec: docs/logic/workspace/roles-and-capabilities.md

// Default persona-to-role mapping. Returning a Role for every Persona value
// keeps the call sites total — no branch ever sees `undefined`.
export function defaultRoleForPersona(persona: Persona): Role {
  switch (persona) {
    case 'Admin':
      return 'admin';
    case 'Editor':
      return 'editor';
    case 'Reviewer':
      return 'reviewer';
    case 'Viewer':
      return 'viewer';
  }
}

// Capabilities per role. Naming convention: colon-namespaced `domain:verb`.
export const CAPABILITIES_BY_ROLE: Record<Role, ReadonlySet<Capability>> = {
  admin: new Set<Capability>([
    'content:write',
    'content:review',
    'roles:admin',
    'see:all',
  ]),
  editor: new Set<Capability>(['content:write', 'see:all']),
  reviewer: new Set<Capability>(['content:review', 'see:all']),
  // Viewers are read-only; they see everything but change nothing.
  viewer: new Set<Capability>(['see:all']),
};

// Human-readable role label — used by the persona dropdown subtitle in the
// TopBar so the user sees both who they are and what they can do.
export function roleLabel(role: Role): string {
  switch (role) {
    case 'admin':
      return 'Admin';
    case 'editor':
      return 'Editor';
    case 'reviewer':
      return 'Reviewer';
    case 'viewer':
      return 'Viewer (read-only)';
  }
}

type RoleContextValue = {
  role: Role;
  can: (capability: Capability) => boolean;
};

const RoleContext = createContext<RoleContextValue | null>(null);

// RoleProvider — derives the active role from the active persona via the
// default mapping above. Mounted by the version entry shell.
export function RoleProvider({ children }: { children: ReactNode }) {
  const { persona } = usePersona();
  const role = useMemo(() => defaultRoleForPersona(persona), [persona]);

  const can = useCallback(
    (capability: Capability) => CAPABILITIES_BY_ROLE[role].has(capability),
    [role],
  );

  const value = useMemo<RoleContextValue>(() => ({ role, can }), [role, can]);

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function useRole(): RoleContextValue {
  const ctx = useContext(RoleContext);
  if (!ctx) {
    // Safe fallback — if a scene mounts outside the RoleProvider (legacy or
    // test harness), default to viewer (least-privileged) so capability
    // checks fail safe. The provider should always be in the tree for normal
    // app boots.
    return {
      role: 'viewer',
      can: (capability: Capability) =>
        CAPABILITIES_BY_ROLE['viewer'].has(capability),
    };
  }
  return ctx;
}
